import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Treebeard } from "react-treebeard";
import { ApiDetails, KEY } from "../../Constants";
import CryptoJS from "crypto-js";
import {
    Box,
    Button,
    Typography,
    Paper,
    Card,
    CardContent,
    CardHeader,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Chip,
    CircularProgress,
    Grid,
    LinearProgress,
    Tooltip,
    useTheme,
    alpha,
    Alert,
    Collapse
} from "@mui/material";

// Icons
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


// Custom TreeBeard theme
const customTreeTheme = {
    tree: {
        base: {
            listStyle: 'none',
            backgroundColor: 'transparent',
            margin: 0,
            padding: 0,
            color: '#212121',
            fontSize: '14px',
            fontFamily: 'inherit'
        },
        node: {
            base: {
                position: 'relative'
            },
            link: {
                cursor: 'pointer',
                position: 'relative',
                padding: '8px 5px',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
            },
            activeLink: {
                background: '#f5f5f5'
            },
            toggle: {
                base: {
                    display: 'inline-block',
                    width: '20px',
                    height: '20px',
                    position: 'relative',
                    verticalAlign: 'middle',
                    marginRight: '10px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    lineHeight: '20px'
                },
                wrapper: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex'
                },
                height: 10,
                width: 10,
                arrow: {
                    fill: '#9da5ab',
                    strokeWidth: 0
                }
            },
            header: {
                base: {
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%'
                },
                connector: {
                    width: '2px',
                    height: '12px',
                    borderLeft: 'solid 2px #9da5ab',
                    borderBottom: 'solid 2px #9da5ab',
                    position: 'absolute',
                    top: 0,
                    left: '-21px'
                },
                title: {
                    lineHeight: '24px',
                    verticalAlign: 'middle',
                    marginLeft: '8px',
                    display: 'flex',
                    alignItems: 'center'
                }
            },
            subtree: {
                listStyle: 'none',
                paddingLeft: '28px'
            }
        }
    }
};

// File size formatter
const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    else return (bytes / 1073741824).toFixed(1) + ' GB';
};

// FileIcon component
const FileIcon = ({ fileName }) => {
    const extension = fileName.split('.').pop().toLowerCase();

    // Determine icon color based on file type
    let color = '#607d8b'; // Default color

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
        color = '#f44336'; // Red for images
    } else if (['pdf'].includes(extension)) {
        color = '#ff9800'; // Orange for PDFs
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
        color = '#2196f3'; // Blue for documents
    } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
        color = '#4caf50'; // Green for spreadsheets
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
        color = '#9c27b0'; // Purple for archives
    }

    return <InsertDriveFileIcon style={{ color }} />;
};

// Enhanced FolderUpload component
const EnhancedFolderUpload = (props) => {
    const theme = useTheme();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fileTree, setFileTree] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertInfo, setAlertInfo] = useState({ type: 'info', message: '' });
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;
    const [landlordId] = useState(() => {
        try {
            const myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            const originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error decrypting data:", error);
            return null;
        }
    });

    useEffect(() => {
        if (props.topBar) {
            props.topBar("");
        }
    }, [props]);

    // File drop handler
    const onDrop = useCallback((acceptedFiles) => {
        // Filter out files larger than 30MB
        const filesWithinLimit = acceptedFiles.filter(
            (file) => file.size <= 30 * 1024 * 1024
        );

        // Check if any files were rejected due to size
        if (acceptedFiles.length !== filesWithinLimit.length) {
            setAlertInfo({
                type: 'warning',
                message: `${acceptedFiles.length - filesWithinLimit.length} file(s) exceeded the 30MB size limit and were not added.`
            });
            setAlertOpen(true);
        }

        // Add files to state
        setUploadedFiles(prevFiles => [...prevFiles, ...filesWithinLimit]);

        // Build a file tree structure
        const targetFolder = "uploads";
        const newFileTree = buildFileTree(filesWithinLimit, targetFolder);
        setFileTree(newFileTree);
    }, []);

    // Build file tree structure
    const buildFileTree = (files, targetFolder) => {
        const fileTree = {
            name: targetFolder,
            toggled: true,
            children: [],
        };

        files.forEach((file) => {
            const pathParts = `${targetFolder}/${file.path}`.split("/");
            let currentNode = fileTree;

            pathParts.forEach((part, index) => {
                if (!currentNode.children) {
                    currentNode.children = [];
                }

                const existingNode = currentNode.children.find(
                    (node) => node.name === part
                );

                if (existingNode) {
                    currentNode = existingNode;
                } else {
                    const isFile = index === pathParts.length - 1;
                    const newNode = {
                        name: part,
                        toggled: !isFile,
                        children: [],
                        isFile: isFile,
                        // Add file info if it's a file
                        ...(isFile && {
                            size: file.size,
                            type: file.type,
                            path: file.path,
                            lastModified: file.lastModified
                        })
                    };
                    currentNode.children.push(newNode);
                    currentNode = newNode;
                }
            });
        });

        return fileTree;
    };

    // Dropzone configuration
    const { getRootProps, getInputProps, isDragActive, isDragReject, isDragAccept } = useDropzone({
        onDrop,
        multiple: true,
        maxSize: 30 * 1024 * 1024, // 30MB limit
    });

    // Tree toggle handler
    const onToggle = (node) => {
        node.toggled = !node.toggled;
        setFileTree({...fileTree});
    };

    // Remove file handler
    const removeFile = (fileToRemove) => {
        const updatedFiles = uploadedFiles.filter(file => file.path !== fileToRemove.path);
        setUploadedFiles(updatedFiles);

        // Rebuild the file tree
        if (updatedFiles.length === 0) {
            setFileTree({});
        } else {
            const targetFolder = "uploads";
            const newFileTree = buildFileTree(updatedFiles, targetFolder);
            setFileTree(newFileTree);
        }
    };

    // Remove all files
    const removeAllFiles = () => {
        setUploadedFiles([]);
        setFileTree({});
    };

    // Upload files
    const uploadFiles = async () => {
        if (uploadedFiles.length === 0) return;

        setIsUploading(true);
        setUploadProgress(0);

        let values = {
            landlord_id: landlordId,
        };

        let formData = new FormData();

        // Append JSON string as a field named 'body'
        formData.append("body", JSON.stringify(values));

        // Append each uploaded file individually
        uploadedFiles.forEach((file, index) => {
            formData.append(`file${index + 1}`, file);
        });

        try {
            // Simulate progress (since fetch doesn't support progress monitoring directly)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    const newProgress = prev + (100 - prev) * 0.1;
                    return newProgress >= 95 ? 95 : newProgress;
                });
            }, 500);

            const response = await fetch(ApiDetails + "pegasus/visionary/files/backup", {
                method: "POST",
                headers: {
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: formData,
            });

            clearInterval(progressInterval);
            setUploadProgress(100);

            const data = await response.json();

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                setAlertInfo({
                    type: 'success',
                    message: 'Files uploaded successfully!'
                });
                setAlertOpen(true);

                setTimeout(() => {
                    setUploadedFiles([]);
                    setFileTree({});
                    if (props.refresh) props.refresh();
                }, 1500);
            } else {
                setAlertInfo({
                    type: 'error',
                    message: 'Upload failed. Please try again.'
                });
                setAlertOpen(true);
            }
        } catch (err) {
            console.error(err);
            setAlertInfo({
                type: 'error',
                message: 'Something went wrong. Please contact customer care for assistance.'
            });
            setAlertOpen(true);
        } finally {
            setTimeout(() => {
                setIsUploading(false);
            }, 500);
        }
    };

    // Get total file size
    const totalSize = uploadedFiles.reduce((acc, file) => acc + file.size, 0);
    const totalSizeFormatted = formatFileSize(totalSize);

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius * 1.5,
                overflow: 'hidden'
            }}
        >
            <CardHeader
                title={
                    <Typography variant="h6" fontWeight="600">
                        File Upload
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" color="text.secondary">
                        Upload files to your account (Max 30MB per file)
                    </Typography>
                }
            />

            <Divider />

            <CardContent>
                {/* Alert messages */}
                <Collapse in={alertOpen}>
                    <Alert
                        severity={alertInfo.type}
                        sx={{ mb: 2 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setAlertOpen(false)}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {alertInfo.message}
                    </Alert>
                </Collapse>

                {/* Drop zone */}
                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragReject
                            ? theme.palette.error.main
                            : isDragAccept
                                ? theme.palette.success.main
                                : isDragActive
                                    ? theme.palette.primary.main
                                    : theme.palette.divider,
                        borderRadius: theme.shape.borderRadius,
                        p: 5,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }
                    }}
                >
                    <input {...getInputProps()} />

                    <CloudUploadIcon
                        sx={{
                            fontSize: 60,
                            color: isDragReject
                                ? theme.palette.error.main
                                : isDragAccept
                                    ? theme.palette.success.main
                                    : theme.palette.primary.main,
                            mb: 2
                        }}
                    />

                    <Typography variant="h6" gutterBottom>
                        {isDragActive
                            ? "Drop files here..."
                            : "Drag & drop files here, or click to select"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {isDragReject
                            ? "Some files are not accepted. Please check file size limits (30MB max)."
                            : "Supports all file types up to 30MB"}
                    </Typography>
                </Box>

                {/* Upload status and actions */}
                {(uploadedFiles.length > 0 || isUploading) && (
                    <Box sx={{ mt: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} selected
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total size: {totalSizeFormatted}
                                </Typography>
                            </Grid>

                            <Grid item>
                                {isUploading ? (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<CircularProgress size={16} />}
                                        disabled
                                    >
                                        Uploading...
                                    </Button>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={removeAllFiles}
                                            disabled={isUploading}
                                        >
                                            Clear All
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CloudUploadIcon />}
                                            onClick={uploadFiles}
                                            disabled={isUploading || uploadedFiles.length === 0}
                                        >
                                            Upload Files
                                        </Button>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>

                        {/* Upload progress */}
                        {isUploading && (
                            <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                    }}
                                />
                                <Typography variant="body2" align="right" sx={{ mt: 0.5 }}>
                                    {Math.round(uploadProgress)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* File list */}
                {uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                            Selected Files
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 200,
                                overflow: 'auto',
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <List dense>
                                {uploadedFiles.map((file, index) => (
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => removeFile(file)}
                                                disabled={isUploading}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <FileIcon fileName={file.path} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={file.path}
                                            secondary={formatFileSize(file.size)}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                style: {
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '400px'
                                                }
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Box>
                )}

                {/* File tree */}
                {Object.keys(fileTree).length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <AccountTreeIcon fontSize="small" />
                            File Structure
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: theme.shape.borderRadius,
                                border: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Treebeard
                                data={fileTree}
                                onToggle={onToggle}
                                style={customTreeTheme}
                            />
                        </Paper>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default EnhancedFolderUpload;